import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Drawer,
  Divider,
  Row,
  Select,
  Form,
  Table,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

import Permissions from '../../auth/Permissions';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import SimpleTextInputModal from '../../common/modals/SimpleTextInputModal';
import DrawerSubmitFooter from '../../common/containers/DrawerSubmitFooter';
import OnTraccrTextInput from '../../common/inputs/OnTraccrTextInput';
import {
  addShortListCustomItem,
  closeShortListDrawer,
  deleteShortListItem,
  getCardById,
} from '../state/boards.actions';
import BorderlessButton from '../../common/buttons/BorderlessButton';
import CustomConfirmModal from '../../common/modals/CustomConfirmModal';
import { getIdMap } from '../../helpers/helpers';

const getOptions = (items) => items.map(({ id, name, ...rest }) => ({
  label: name,
  value: id,
  ...rest,
}));

export default function ShortListCardDrawer() {
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const [addType, setAddType] = useState();
  const [userShortLists, setUserShortLists] = useState([]);
  const [userShortListCategories, setUserShortListCategories] = useState([]);
  const [selectedShortListId, setSelectedShortListId] = useState();
  const [selectedShortListCategoryId, setSelectedShortListCategoryId] = useState();

  const shortLists = useSelector((state) => state.boards.shortLists);
  const shortListCategories = useSelector((state) => state.boards.shortListCategories);
  const selectedCard = useSelector((state) => state.boards.selectedCard);
  const shortListPermissionMap = useSelector((state) => state.boards.shortListPermissionMap);
  const {
    visible = false,
    selectedCardId: shortListCardId,
  } = useSelector((state) => state.boards.shortListDrawerConfig);

  useEffect(() => {
    if (visible && shortListCardId) dispatch(getCardById(shortListCardId));
  }, [shortListCardId, visible]);

  const shortListItems = useMemo(() => (
    shortListCardId && shortListCardId === selectedCard?.id ? selectedCard.shortListItems : []
  ), [selectedCard, shortListCardId]);

  useEffect(() => {
    const selectedShortLists = new Set(
      shortListItems?.map(({ boardShortListId }) => boardShortListId)
    );
    setUserShortLists((prev) => {
      const existingOptions = shortLists.filter(
        ({ isOwned, id }) => (
          (
            isOwned
            || shortListPermissionMap?.[id]?.some(({ canEdit, userId }) => canEdit && userId === Permissions.id)
          )
          && !selectedShortLists.has(id)
        ),
      );
      const customOptions = prev.filter(({ isCustom }) => isCustom);
      return [...getOptions(existingOptions), ...customOptions];
    });
    setUserShortListCategories((prev) => {
      const customOptions = prev.filter(({ isCustom }) => isCustom);
      return [...getOptions(shortListCategories), ...customOptions];
    });
  }, [shortLists, shortListCategories, shortListItems, shortListPermissionMap]);

  const shortListMap = useMemo(() => getIdMap(shortLists), [shortLists]);
  const categoryMap = useMemo(() => getIdMap(shortListCategories), [shortListCategories]);

  const userShortListCategoryMap = useMemo(() => (
    userShortListCategories.reduce((acc, category) => {
      const { boardShortListId } = category;
      if (!(boardShortListId in acc)) {
        acc[boardShortListId] = [];
      }
      acc[boardShortListId].push(category);
      return acc;
    }, {})
  ), [userShortListCategories]);

  const onAddClicked = useCallback((type) => () => {
    setAddType(type);
  }, []);

  const dropdownRenderHandler = useCallback((type) => (menu) => (
    <div style={{ width: '100%', padding: 5 }}>
      {menu}
      <Divider style={{ margin: '4px 0px 8px 0px' }} />
      <Row justify="end">
        <OnTraccrButton
          icon={ <PlusOutlined />}
          onClick={onAddClicked(type)}
          title="Add"
        />
      </Row>
    </div>
  ), []);

  const onShortListChange = useCallback((selectedId) => {
    setSelectedShortListId(selectedId);
    setSelectedShortListCategoryId();
    form.setFieldsValue({
      shortListId: selectedId,
      shortListCategoryId: null,
    });
  }, [form]);

  const onShortListCategoryChange = useCallback((selectedId) => {
    setSelectedShortListCategoryId(selectedId);
    form.setFieldsValue({
      shortListCategoryId: selectedId,
    });
  }, [form]);

  const onAddNewEntity = useCallback(async (newValue) => {
    if (addType === 'Short List') {
      setUserShortLists(userShortLists.concat({
        label: newValue,
        value: `new-${newValue}`,
        isCustom: true,
      }));
      onShortListChange(`new-${newValue}`);
    } else {
      setUserShortListCategories(userShortListCategories.concat({
        label: newValue,
        value: `new-${newValue}`,
        boardShortListId: selectedShortListId,
        isCustom: true,
      }));
      onShortListCategoryChange(`new-${newValue}`);
    }

    setAddType();
  }, [
    addType,
    userShortLists,
    userShortListCategories,
    selectedShortListId,
    onShortListChange,
    onShortListCategoryChange,
  ]);

  const onSubmit = useCallback(async () => {
    await form.validateFields();
    const values = form.getFieldsValue();

    const payload = {
      description: values.description,
      shortListId: selectedShortListId,
      shortListCategoryId: selectedShortListCategoryId,
    };

    if (selectedShortListId?.startsWith('new-')) {
      payload.shortListId = null;
      payload.shortListName = selectedShortListId.replace('new-', '');
    }
    if (selectedShortListCategoryId?.startsWith('new-')) {
      payload.shortListCategoryId = null;
      payload.shortListCategoryName = selectedShortListCategoryId.replace('new-', '');
    }

    if (await dispatch(addShortListCustomItem(shortListCardId, payload))) {
      form.resetFields();

      if (payload.shortListName) {
        setUserShortLists((prev) => prev.filter(({ value }) => value !== selectedShortListId));
      }

      if (payload.shortListCategoryName) {
        setUserShortListCategories(
          (prev) => prev.filter(({ value }) => value !== selectedShortListCategoryId)
        );
      }

      setAddType();
      setSelectedShortListId();
      setSelectedShortListCategoryId();
    }
  }, [form, selectedShortListId, selectedShortListCategoryId, shortListCardId]);

  const onDelete = useCallback((record) => {
    CustomConfirmModal({
      title: 'Delete Short List Item',
      content: 'Are you sure you want to remove this item from the short list?',
      onOk: () => dispatch(deleteShortListItem(record.boardShortListId, record.id)),
    });
  }, []);

  const tableColumns = useMemo(() => [
    {
      title: 'Short List',
      dataIndex: 'name',
      render: (_, record) => (
        record.name ? record.name : (shortListMap[record.boardShortListId]?.name || '')
      ),
    },
    {
      title: 'Category',
      dataIndex: 'categoryName',
      render: (_, record) => (
        record.categoryName ? record.categoryName : (categoryMap[record.boardShortListCategoryId]?.name || '')
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
    },
    {
      title: '',
      dataIndex: 'delete',
      render: (_, record) => (
        (
          record.userId === Permissions.id
          || shortListPermissionMap?.[record.boardShortListId]?.some(({ canEdit, userId }) => canEdit && userId === Permissions.id)
         ) && (
          <BorderlessButton
            iconNode={<DeleteOutlined style={{ color: 'red' }} />}
            onClick={() => onDelete(record)}
          />
        )
      ),
    },
  ], [onDelete, categoryMap, shortListMap, shortListPermissionMap]);

  const onClose = useCallback(() => {
    dispatch(closeShortListDrawer());
  }, []);

  return (
    <Drawer
      title="Short Lists"
      visible={visible}
      onClose={onClose}
      width={600}
      bodyStyle={{
        padding: '0px 24px',
      }}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 10}}>
        <Form.Item
          label="Short List"
          name="shortListId"
          rules={[{ required: true, message: 'Short List is required' }]}
        >
          <Select
            options={userShortLists}
            onSelect={onShortListChange}
            dropdownRender={dropdownRenderHandler('Short List')}
          />
        </Form.Item>
        <Form.Item
          label="Short List Category"
          name="shortListCategoryId"
        >
          <Select
            options={userShortListCategoryMap?.[selectedShortListId]}
            onSelect={onShortListCategoryChange}
            dropdownRender={dropdownRenderHandler('Short List Category')}
          />
        </Form.Item>
        <Form.Item
          label="Description"
          name="description"
        >
          <OnTraccrTextInput
            style={{ width: '100%' }}
            textarea
          />
        </Form.Item>
      </Form>
      <Table
        dataSource={shortListItems}
        columns={tableColumns}
        rowKey="id"
      />
      <SimpleTextInputModal
        title={`Add ${addType}`}
        visible={!!addType}
        onClose={() => setAddType()}
        placeholder={`Enter ${addType}`}
        onSave={onAddNewEntity}
      />
      <DrawerSubmitFooter
        onClose={onClose}
        onSubmit={onSubmit}
      />
    </Drawer>
  );
};
